import { useState } from "react";
import { motion } from "framer-motion";
import { MaskLines } from "./Reveal";

const PATHS = [
  {
    value: "build-scale",
    number: "01",
    title: ["Build", "& Scale"],
    statement: "We're building what's next.",
    support:
      "For businesses looking to increase capacity, improve efficiency, and create the foundation for sustainable growth.",
    img: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?q=80&w=1600&auto=format&fit=crop",
    alt: "Builder overlooking an active construction site — building what's next",
  },
  {
    value: "transform-optimize",
    number: "02",
    title: ["Transform", "& Optimize"],
    statement: "We're transforming what already exists.",
    support:
      "For established organizations looking to simplify complexity, optimize operations, and unlock significant value.",
    img: "https://images.unsplash.com/photo-1487958449943-2429e8be8625?q=80&w=1600&auto=format&fit=crop",
    alt: "Large-scale architectural facade — transforming existing systems",
  },
];

const ease = [0.16, 1, 0.3, 1];

const EntryExperience = ({ onSelect }) => {
  const [chosen, setChosen] = useState(null);

  const choose = (value) => {
    if (chosen) return;
    setChosen(value);
    setTimeout(() => onSelect(value), 450);
  };

  return (
    <motion.div
      data-testid="entry-experience"
      role="dialog"
      aria-label="Choose your perspective"
      className="grain fixed inset-0 z-[100] flex flex-col overflow-y-auto overscroll-contain bg-ink text-white"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.7, ease } }}
    >
      {/* Wordmark */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        className="mx-auto flex w-full max-w-[1500px] items-center justify-between px-6 pt-8 lg:px-12"
      >
        <span className="font-serif text-2xl font-semibold tracking-tight">Prospera</span>
        <span className="hidden font-mono text-[10px] uppercase tracking-[0.35em] text-white/40 sm:block">
          AI Business Optimization Firm
        </span>
      </motion.div>

      {/* Headline */}
      <div className="mx-auto w-full max-w-[1500px] px-6 pt-14 lg:px-12 lg:pt-20">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.35 }}
          className="mb-6 flex items-center gap-4"
        >
          <span className="h-px w-12 bg-cobalt" />
          <span className="font-mono text-xs uppercase tracking-[0.35em] text-white/60">
            Begin here
          </span>
        </motion.div>
        <h1 className="font-serif text-4xl font-medium uppercase leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl">
          <MaskLines lines={["Where are", "you going?"]} delay={0.45} />
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9, ease }}
          className="mt-6 max-w-md text-base font-light leading-relaxed text-white/60"
        >
          Every business has a different starting point.
          <br />
          The objective is the same: move forward.
        </motion.p>
      </div>

      {/* Pathways */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 1.05, ease }}
        className="mt-12 flex-1 border-t border-white/10 lg:mt-16"
      >
        <div className="grid h-full grid-cols-1 lg:grid-cols-2">
          {PATHS.map((p, i) => (
            <button
              key={p.value}
              data-testid={`entry-path-${p.value}`}
              onClick={() => choose(p.value)}
              aria-label={`${p.title.join(" ")} — ${p.statement}`}
              className={`group relative flex min-h-[34vh] flex-col justify-between overflow-hidden border-b border-white/10 p-8 text-left transition-opacity duration-500 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-cobalt lg:min-h-0 lg:border-b-0 lg:p-12 ${
                i === 0 ? "lg:border-r lg:border-white/10" : ""
              } ${chosen && chosen !== p.value ? "opacity-30" : ""}`}
            >
              {/* Hover / selected imagery */}
              <div
                className={`absolute inset-0 transition-opacity duration-700 ${
                  chosen === p.value ? "opacity-30" : "opacity-0 group-hover:opacity-25"
                }`}
                aria-hidden="true"
              >
                <img src={p.img} alt="" className="h-full w-full scale-105 object-cover transition-transform duration-[1200ms] group-hover:scale-100" />
                <div className="absolute inset-0 bg-cobalt-deep/60 mix-blend-multiply" />
              </div>
              <div
                className={`absolute inset-x-0 bottom-0 h-[3px] bg-cobalt transition-transform duration-500 origin-left ${
                  chosen === p.value ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                }`}
                aria-hidden="true"
              />

              <div className="relative">
                <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-white/40">
                  Path {p.number}
                </span>
                <span className="mt-6 block font-serif text-4xl font-medium uppercase leading-[1.02] tracking-tight transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:translate-x-2 sm:text-5xl">
                  {p.title.map((line) => (
                    <span key={line} className="block">
                      {line}
                    </span>
                  ))}
                </span>
                <span className="mt-5 block font-serif text-lg italic text-cobalt-200 text-white/80">
                  {p.statement}
                </span>
              </div>

              <div className="relative mt-10">
                <p className="max-w-sm text-sm font-light leading-relaxed text-white/55">
                  {p.support}
                </p>
                <span className="mt-6 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 transition-colors duration-300 group-hover:text-white">
                  Select this path
                  <span className="inline-block transition-transform duration-300 group-hover:translate-x-2">→</span>
                </span>
              </div>
            </button>
          ))}
        </div>
      </motion.div>

      {/* Neutral option */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.9, delay: 1.35 }}
        className="border-t border-white/10 py-6 text-center"
      >
        <button
          data-testid="entry-explore-link"
          onClick={() => choose("explore")}
          className="px-4 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-white/45 underline-offset-4 transition-colors duration-300 hover:text-white hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-cobalt"
        >
          Not sure where you fit? Explore Prospera →
        </button>
      </motion.div>
    </motion.div>
  );
};

export default EntryExperience;
